import { useBlinkContext, } from '@thoughtspot/blink-context';
import { calculateTooltipPosition, } from '@thoughtspot/chart-common';
import { generateUUID } from '@thoughtspot/js-util';
import { create } from '@thoughtspot/logger';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
import { ChartToTSEvent, TSToChartEvent, } from '@thoughtspot/ts-chart-sdk';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { getCustomChartContext, setCustomChartContext, } from '../context/custom-chart-app-context';
import { EventProcessor, } from '../events/chart-to-ts/event-processor';
import { NativeChartToTSEvent } from '../events/chart-to-ts/event-processor.types';
import { setupMuzeAiHostBridge } from '../events/chart-to-ts/native-chart-events/muze-studio-codegen/host-bridge';
import { EventEmitter } from '../events/ts-to-chart/event-emitter';
import { registerChartComponent as registerChartComponentListener } from '../services/chart-app-event-bridge';
import styles from '../styles/chart.module.scss';
import { generateAppConfig } from '../utils/app-config.util';
import { getIsChartSettingsV2EnabledValue } from '../utils/chart-settings/custom-chart-schema-hydrator.util';
import { sanitizeCustomVisualPropsV2 } from '../utils/chart-settings/helpers';
import { getChartType, isCustomChartType } from '../utils/common-util';
import { areChartConfigEqual, isConditionalFormattingEqual, mapSDKConfigToCustomChartConfig, parseChartConfigToCustomChartConfigInput, } from '../utils/custom-chart-config-adaptor.util';
import { sanitizeCustomVisualProps } from '../utils/form-builder.util';
import { OtelOp } from '../utils/otel-operations';
import { childContext, createFinishedSpan, startSpan } from '../utils/tracer';
import { useChartFrame } from '../utils/use-chart-frame';
import { ChartErrorComponent } from './chart-error/chart-error.component';
import { ErrorTypes } from './chart-error/chart-error.helper';
import { convertErrorObjectToChartErrorObject, getErrorObject, } from './chart-error/chart-error.util';
import { hasColumnNameChanged, updateChartVizRefColumns, updateChartVizRefConfig, updateCharVizRefColumnProperties, } from './custom-chart.component.util';
import CustomGlobalAlertBannerComponent from './global-alert/banner/banner';
import CustomGlobalAlertToastComponent from './global-alert/toast/toast';
import { TooltipComponent } from './tooltip/tooltip';
const logger = create('TSChartApp');
const CHART_RENDER_TIMEOUT_INTERVAL_FOR_TS_HOSTED_CHART = 90 * 1000;
const CHART_RENDER_TIMEOUT_INTERVAL_FOR_NON_TS_HOSTED_CHART = 60 * 1000;
const CHART_INIT_START_TIMEOUT_INTERVAL = 30 * 1000;
const CHART_RENDER_IN_PROGRESS_TIMEOUT_INTERVAL = 30 * 1000;
const IFRAME_SPAN_MSG_TYPE = '__ts_valkyrie_span__';
const DYOC_OTEL_MSG_TYPE = '__ts_dyoc_otel__';
export const CustomChartComponentInner = ({ viz, isPrintMode, isTsHostedChart, isEmbedded = false, onChartRenderComplete, getAnswerDataByQuery, openContextMenu, closeContextMenu, actionHandler, openCustomChartMenu, closeCustomChartMenu, updateCustomChartConfig, updateCustomChartConfigUserAction, updateCustomVisualProps, getTMLString, setTMLString, setAnswerAlertBanner, isLiveboardContext, isSpotterContext, customStylingService, configOptions, setAdvancedPivotTableXlsxDownloadCb, getEmbedCssStyles, setCustomChartLoadingInProgressInfo, onCustomChartError, onPointRightClick, onPointClick, onPointDoubleClick, queryMetricsRef, isInErrorState = false, otelContext = null, otelEnabled = false, }) => {
    const { metricsService } = useBlinkContext();
    const embedCssStyles = getEmbedCssStyles();
    const componentId = useMemo(() => generateUUID(), []);
    const [onEventCallBackFn, setOnEventCallBackFn] = useState();
    const [isInitialized, setIsInitialized] = useState(false);
    const [renderComplete, setIsRenderComplete] = useState(false);
    const customChartLoadingInProgressMessageRef = useRef(null);
    const appConfigRef = useRef(null);
    const errorStateRef = useRef(false);
    const [error, setError] = useState(null);
    const [tooltipConfig, setTooltipConfig] = useState({
        content: {},
        customTooltipContent: [],
    });
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const { visualPropEditorDefinition, chartConfigEditorDefinition, globalPersistedKeyVisualProp, } = getCustomChartContext();
    const { setChartConfigEditorDefiniton, setValidateChartConfigCB, setValidateCustomVisualPropsCB, setDispatchCustomChartMixpanelEventCB, setVisualPropEditorDefinition, setChartConfigQueries, setAllConfigs, } = setCustomChartContext();
    const chartType = getChartType(viz);
    const tooltipEl = useRef(null);
    const vizRef = useRef(viz);
    const renderTimeoutRef = useRef(null);
    const initStartTimeoutRef = useRef(null);
    const iframeSpanRef = useRef(null);
    const replayParentCtxRef = useRef(null);
    const iframeContentWindowRef = useRef(null);
    const containerRef = useRef();
    const otelContextRef = useRef(otelContext);
    const otelEnabledRef = useRef(otelEnabled);
    useEffect(() => {
        otelContextRef.current = otelContext;
        otelEnabledRef.current = otelEnabled;
    }, [otelContext, otelEnabled]);
    const pendingIframeSpansRef = useRef(new Map());
    useEffect(() => {
        if (!otelEnabled)
            return undefined;
        function replaySpans() {
            const spans = pendingIframeSpansRef.current;
            const replayCtx = replayParentCtxRef.current ?? otelContextRef.current;
            if (!replayCtx) {
                spans.clear();
                return;
            }
            const byId = new Map();
            spans.forEach(s => byId.set(s.spanId, s));
            const childrenMap = new Map();
            spans.forEach(s => {
                const key = (s.parentSpanId && byId.has(s.parentSpanId))
                    ? s.parentSpanId
                    : undefined;
                if (!childrenMap.has(key))
                    childrenMap.set(key, []);
                childrenMap.get(key).push(s);
            });
            const dfs = (parentKey, parentCtx) => {
                const children = childrenMap.get(parentKey) ?? [];
                for (const s of children.sort((a, b) => a.startMs - b.startMs)) {
                    const childCtx = createFinishedSpan(s.name, s.startMs, s.endMs, parentCtx, s.attributes);
                    if (childCtx)
                        dfs(s.spanId, childCtx);
                }
            };
            dfs(undefined, replayCtx);
            spans.clear();
        }
        function handleMessage(event) {
            const data = event.data;
            if (data?.type !== IFRAME_SPAN_MSG_TYPE)
                return;
            const knownWindow = iframeContentWindowRef.current
                ?? chartIframeElRef?.current?.contentWindow;
            if (knownWindow && event.source !== knownWindow)
                return;
            pendingIframeSpansRef.current.set(data.spanId, data);
            if (data.name?.endsWith('.lifecycle'))
                replaySpans();
        }
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [otelEnabled]);
    const { chartIframeElRef, targetFrameUrl, chartJSX, elementId, } = useChartFrame(componentId, viz, () => { }, onIFrameErrorCallback, isPrintMode, isLiveboardContext);
    const { emitEvent } = EventEmitter({
        componentId,
        appUrl: targetFrameUrl,
        iFrameWindow: chartIframeElRef,
        setError: (errorMsg) => {
            setAnswerAlertBanner(errorMsg, BannerAlertType.ERROR);
        },
        chartType,
        metricsService,
    });
    const updateVisualPropEditorDefinitionContext = ({ updatedVisualPropEditorDefinition, currentVisualPropEditorDefinition = visualPropEditorDefinition, }) => {
        const currentStr = JSON.stringify(currentVisualPropEditorDefinition);
        const updatedStr = JSON.stringify(updatedVisualPropEditorDefinition);
        if (currentStr !== updatedStr) {
            setVisualPropEditorDefinition(updatedVisualPropEditorDefinition);
        }
    };
    const updateCustomChartContext = ({ updatedVisualPropEditorDefinition, updatedChartConfigEditorDefinition, currentVisualPropEditorDefinition = visualPropEditorDefinition, currentChartConfigEditorDefinition = chartConfigEditorDefinition, }) => {
        const currentVisualStr = JSON.stringify(currentVisualPropEditorDefinition);
        const updatedVisualStr = JSON.stringify(updatedVisualPropEditorDefinition);
        if (currentVisualStr !== updatedVisualStr) {
            setVisualPropEditorDefinition(updatedVisualPropEditorDefinition);
        }
        const currentChartStr = JSON.stringify(currentChartConfigEditorDefinition);
        const updatedChartStr = JSON.stringify(updatedChartConfigEditorDefinition);
        if (currentChartStr !== updatedChartStr) {
            setChartConfigEditorDefiniton(updatedChartConfigEditorDefinition);
        }
    };
    const validateChartConfig = useCallback(async (config) => {
        return emitEvent(TSToChartEvent.ChartConfigValidate, {
            customChartConfig: config,
            columns: viz.columns,
            columnProperties: viz.vizProp.columnProperties,
            updateCustomChartContext,
        });
    }, [viz.columns, chartConfigEditorDefinition]);
    const validateVisualProps = useCallback(async (visualProps, activeColumnId, changeInfo) => {
        return emitEvent(TSToChartEvent.VisualPropsValidate, {
            visualProps,
            updateCustomChartContext,
            activeColumnId,
            changeInfo,
        });
    }, [viz.customVisualProps, visualPropEditorDefinition]);
    const getColumnDataFromSdk = useCallback(async (props) => {
        return emitEvent(TSToChartEvent.GetColumnData, props);
    }, [viz.columns]);
    const dispatchCustomChartMixpanelEvent = useCallback(async (payload) => {
        return emitEvent(TSToChartEvent.MixpanelEvent, payload);
    }, []);
    const updateChartContext = (chartConfigEditorDefinition, visualPropEditorDefinition, sdkChartConfig, allowedConfigurations, chartConfigParameters, persistedVisualPropKeys) => {
        if (!_.isNil(sdkChartConfig) && !_.isEmpty(sdkChartConfig)) {
            updateChartVizRefConfig(vizRef, sdkChartConfig);
            updateCustomChartConfig(parseChartConfigToCustomChartConfigInput(sdkChartConfig), true);
        }
        logger.debug('chartConfigEditorDefintion post init', chartConfigEditorDefinition, 'visualPropEditorDefinition post init', visualPropEditorDefinition);
        setAllConfigs({
            chartConfigEditorDefinition,
            visualPropEditorDefinition,
            allowedConfigurations,
            persistedVisualPropKeys,
            chartConfigParameters,
            validateCustomChartConfig: validateChartConfig,
            validateCustomVisualProps: validateVisualProps,
            getColumnData: getColumnDataFromSdk,
            dispatchCustomChartMixpanelEvent,
        });
    };
    const emitAdvancedPivotTableXlsxDownloadToChart = useCallback(async (answerTitle) => {
        return emitEvent(TSToChartEvent.DownloadExcelTrigger, {
            answerTitle,
        });
    }, []);
    useEffect(() => {
        if (isInErrorState) {
            errorStateRef.current = true;
        }
    }, [isInErrorState]);
    useEffect(() => {
        if (isInitialized &&
            _.isFunction(setAdvancedPivotTableXlsxDownloadCb)) {
            setAdvancedPivotTableXlsxDownloadCb(emitAdvancedPivotTableXlsxDownloadToChart);
        }
    }, [isInitialized]);
    useEffect(() => {
        setValidateCustomVisualPropsCB(validateVisualProps);
        setValidateChartConfigCB(validateChartConfig);
    }, [visualPropEditorDefinition, chartConfigEditorDefinition]);
    useEffect(() => {
        setDispatchCustomChartMixpanelEventCB(dispatchCustomChartMixpanelEvent);
    }, [dispatchCustomChartMixpanelEvent]);
    function clearInitAndRenderTimeouts() {
        if (isTsHostedChart) {
            clearTimeout(initStartTimeoutRef?.current);
            clearTimeout(renderTimeoutRef.current);
            renderTimeoutRef.current = null;
            initStartTimeoutRef.current = null;
        }
    }
    const [renderTimeoutFired, setRenderTimeoutFired] = useState(false);
    useEffect(() => {
        if (renderTimeoutFired && isTsHostedChart) {
            setCustomChartLoadingInProgressInfo({ isLoading: false });
        }
    }, [renderTimeoutFired]);
    function createRenderTimeout() {
        clearTimeout(renderTimeoutRef.current);
        renderTimeoutRef.current = null;
        renderTimeoutRef.current = setTimeout(() => {
            logger.debug('finalRenderTimeout');
            setIsRenderComplete(currentRenderComplete => {
                if (currentRenderComplete) {
                    return currentRenderComplete;
                }
                iframeSpanRef.current?.setAttribute('iframe.error_type', ErrorTypes.CHART_RENDER_TIMEOUT);
                iframeSpanRef.current?.setStatus({ code: 2 });
                iframeSpanRef.current?.end();
                iframeSpanRef.current = null;
                setError(getErrorObject(ErrorTypes.CHART_RENDER_TIMEOUT, chartType));
                clearTimeout(initStartTimeoutRef.current);
                setRenderTimeoutFired(true);
                onChartRenderComplete();
                setCustomChartLoadingInProgressInfo({ isLoading: false });
                return currentRenderComplete;
            });
        }, CHART_RENDER_TIMEOUT_INTERVAL_FOR_TS_HOSTED_CHART);
    }
    function createInitAndRenderTimeouts() {
        clearTimeout(initStartTimeoutRef.current);
        initStartTimeoutRef.current = null;
        initStartTimeoutRef.current = setTimeout(() => {
            logger.debug('Render (First Timeout)');
            setIsRenderComplete(currentRenderComplete => {
                if (!currentRenderComplete) {
                    logger.log('Rendering taking longer than expected...', viz);
                    setCustomChartLoadingInProgressInfo({
                        isLoading: true,
                        loadingMessage: customChartLoadingInProgressMessageRef.current ??
                            undefined,
                    });
                }
                return currentRenderComplete;
            });
        }, CHART_RENDER_IN_PROGRESS_TIMEOUT_INTERVAL);
        createRenderTimeout();
    }
    async function onInitStartCallback() {
        iframeSpanRef.current = startSpan(OtelOp.RENDER_IFRAME_LIFECYCLE, otelContext, otelEnabled, {
            'iframe.is_ts_hosted': isTsHostedChart,
            'iframe.chart_type': chartType,
            'iframe.timeout_render_ms': isTsHostedChart
                ? CHART_RENDER_TIMEOUT_INTERVAL_FOR_TS_HOSTED_CHART
                : CHART_RENDER_TIMEOUT_INTERVAL_FOR_NON_TS_HOSTED_CHART,
        });
        replayParentCtxRef.current = childContext(otelContext, iframeSpanRef.current);
        iframeContentWindowRef.current = chartIframeElRef?.current?.contentWindow ?? null;
        iframeContentWindowRef.current?.postMessage({ type: DYOC_OTEL_MSG_TYPE, otelEnabled }, '*');
        if (isTsHostedChart) {
            createInitAndRenderTimeouts();
        }
        setIsInitialized(false);
        logger.debug(`IFrame ready: ${componentId}`);
        const appConfig = await generateAppConfig(isPrintMode, isLiveboardContext, targetFrameUrl, customStylingService, viz?.config?.customChartManifest?.chartAppAccessToken, embedCssStyles, isTsHostedChart, queryMetricsRef?.current || undefined, isEmbedded, isSpotterContext);
        const initStart = Date.now();
        const pmInitSpan = startSpan(OtelOp.NETWORK_POSTMESSAGE_INIT, childContext(otelContext, iframeSpanRef.current), otelEnabled, { 'postmessage.event_type': TSToChartEvent.Initialize });
        emitEvent(TSToChartEvent.Initialize, {
            chartModel: viz,
            renderComplete,
            appConfig,
            hostUrl: window.location.origin,
            containerElSelector: `#${elementId}`,
            getAnswerDataByQuery,
            updateChartContext,
            updateCustomChartContext,
            configOptions: { configOptions },
            setChartConfigQueries,
            updateCustomVisualProps,
            otelContext: replayParentCtxRef.current ?? otelContext,
            otelEnabled,
        })
            .then(res => {
            pmInitSpan.setAttribute('postmessage.duration_ms', Date.now() - initStart);
            pmInitSpan.end();
            appConfigRef.current = appConfig;
            setIsInitialized(true);
            if (isTsHostedChart) {
                return res;
            }
            renderTimeoutRef.current = setTimeout(() => {
                setIsRenderComplete(currentRenderComplete => {
                    if (currentRenderComplete) {
                        return currentRenderComplete;
                    }
                    iframeSpanRef.current?.setAttribute('iframe.error_type', ErrorTypes.CHART_RENDER_TIMEOUT);
                    iframeSpanRef.current?.setStatus({ code: 2 });
                    iframeSpanRef.current?.end();
                    iframeSpanRef.current = null;
                    setError(getErrorObject(ErrorTypes.CHART_RENDER_TIMEOUT, chartType));
                    onChartRenderComplete();
                    return currentRenderComplete;
                });
            }, CHART_RENDER_TIMEOUT_INTERVAL_FOR_NON_TS_HOSTED_CHART);
            return res;
        })
            .catch(err => {
            pmInitSpan.setStatus({ code: 2 });
            pmInitSpan.end();
            iframeSpanRef.current?.setAttribute('iframe.error_type', ErrorTypes.CHART_INITIALIZATION_ERROR);
            iframeSpanRef.current?.setStatus({ code: 2 });
            iframeSpanRef.current?.end();
            iframeSpanRef.current = null;
            logger.error('Error in initializing chart', err);
            if (err?.code) {
                setError(convertErrorObjectToChartErrorObject(err));
            }
            else if (err?.errorType) {
                setError(getErrorObject(err.errorType, chartType));
            }
            else {
                setError(getErrorObject(err?.errorType ??
                    ErrorTypes.CHART_INITIALIZATION_ERROR, chartType));
            }
            clearInitAndRenderTimeouts();
            onChartRenderComplete();
        });
    }
    function handleCustomChartError(err, errorType = ErrorTypes.IFRAME_RENDER_ERROR) {
        if (onCustomChartError) {
            try {
                onCustomChartError({
                    errorType,
                    errorMessage: err?.message || 'Unknown custom chart error',
                    errorStack: err?.stack || '',
                    chartType,
                    vizId: viz?.id,
                    timestamp: Date.now(),
                });
            }
            catch (error) {
                logger.warn('Failed to handle custom chart error', error);
            }
        }
        else {
            logger.warn('Custom chart error handler not configured');
        }
    }
    async function onIFrameErrorCallback(err) {
        iframeSpanRef.current?.setAttribute('iframe.error_type', ErrorTypes.IFRAME_RENDER_ERROR);
        iframeSpanRef.current?.setStatus({ code: 2 });
        iframeSpanRef.current?.end();
        iframeSpanRef.current = null;
        handleCustomChartError(err);
        logger.error('Error in rendering application in the iframe', err);
        clearInitAndRenderTimeouts();
        setError(getErrorObject(ErrorTypes.IFRAME_RENDER_ERROR, chartType));
        onChartRenderComplete();
    }
    const showToolTip = (event, customTooltipContent = [], iframeElement, convertedRecord) => {
        setIsTooltipVisible(true);
        const positionAttrs = calculateTooltipPosition(tooltipEl.current, iframeElement, [event.clientX, event.clientY]);
        setTooltipConfig({
            content: convertedRecord,
            ...positionAttrs,
            customTooltipContent,
        });
    };
    const hideToolTip = () => {
        setIsTooltipVisible(false);
    };
    const updateChartConfig = async (sdkChartConfig, skipRerender, isUserAction) => {
        updateChartVizRefConfig(vizRef, sdkChartConfig);
        const configUpdater = isUserAction
            ? updateCustomChartConfigUserAction
            : updateCustomChartConfig;
        const customChartConfigInput = parseChartConfigToCustomChartConfigInput(sdkChartConfig);
        if (skipRerender) {
            await configUpdater(customChartConfigInput, true);
        }
        else {
            await configUpdater(customChartConfigInput, false);
        }
        const customChartConfig = mapSDKConfigToCustomChartConfig(sdkChartConfig);
        await validateChartConfig(customChartConfig);
    };
    const emitVisualPropsUpdate = useCallback(() => {
        emitEvent(TSToChartEvent.VisualPropsUpdate, {
            visualProps: viz.customVisualProps,
            updateChartModelProps: {
                chartModel: viz,
                renderComplete,
                getAnswerDataByQuery,
                options: { configOptions },
                setChartConfigQueries,
            },
        });
    }, [JSON.stringify(viz)]);
    useEffect(() => {
        if (!isInitialized) {
            return;
        }
        if (!_.isEmpty(viz?.visualOverrides?.overrides) ||
            !_.isEmpty(viz?.visualOverrides?.defaults)) {
            onInitStartCallback();
        }
    }, [JSON.stringify(viz?.visualOverrides)]);
    useEffect(() => {
        if (!isInitialized) {
            return;
        }
        logger.debug('VisualPropsUpdate triggered from component', viz.customVisualProps);
        if (errorStateRef.current) {
            errorStateRef.current = false;
            onInitStartCallback();
        }
        else {
            emitVisualPropsUpdate();
        }
    }, [JSON.stringify(viz?.customVisualProps)]);
    useEffect(() => {
        if (!isInitialized) {
            return;
        }
        const isConfigEqual = areChartConfigEqual(vizRef.current.config.customChartConfig, viz.config.customChartConfig);
        const isColumnsEqual = _.isEqual(vizRef.current.columns, viz.columns);
        const isCfEqual = !isCustomChartType(getChartType(viz)) ||
            isConditionalFormattingEqual(vizRef.current.vizProp.columnProperties, viz.vizProp.columnProperties);
        const isSortInfoEqual = _.isEqual(vizRef.current.sortInfo, viz.sortInfo);
        const columnNameChanged = hasColumnNameChanged(vizRef.current.columns, viz.columns);
        if (!isCfEqual) {
            updateCharVizRefColumnProperties(vizRef, viz);
        }
        if (!isColumnsEqual) {
            updateChartVizRefColumns(vizRef, viz);
        }
        logger.debug('ChartModelUpdate triggered from component', viz.config.customChartConfig, viz.columns, isConfigEqual, isColumnsEqual, isCfEqual);
        if (!isConfigEqual ||
            !isColumnsEqual ||
            !isCfEqual ||
            !isSortInfoEqual) {
            emitEvent(TSToChartEvent.ChartModelUpdate, {
                chartModel: viz,
                getAnswerDataByQuery,
                renderComplete,
                options: {
                    ...configOptions,
                    appConfig: appConfigRef.current,
                },
                setChartConfigQueries,
                visualPropEditorDefinition,
                otelContext: replayParentCtxRef.current ?? otelContext,
                otelEnabled,
            })
                .then(() => {
                if (columnNameChanged) {
                    validateVisualProps(viz.customVisualProps, undefined);
                }
                return undefined;
            })
                .catch(err => {
                logger.error('Error in chart model update', err);
            });
        }
    }, [
        viz.config.customChartConfig,
        viz.columns,
        viz.vizProp.columnProperties,
        viz.sortInfo,
    ]);
    useEffect(() => {
        if (onEventCallBackFn) {
            onEventCallBackFn(ChartToTSEvent.UpdateVisualProps, {
                visualPropEditorDefinition,
                globalPersistedKeyVisualProp,
                updateVisualProps: (visualProp, isSystemAction, visualPropEditorDefinition, globalPersistedKeyVisualProp) => {
                    let currentVisualProp;
                    if (typeof visualProp === 'string') {
                        currentVisualProp = DOMPurify.sanitize(visualProp);
                    }
                    else if (typeof visualProp === 'object') {
                        const isChartSettingsV2 = getIsChartSettingsV2EnabledValue(appConfigRef.current);
                        if (isChartSettingsV2) {
                            currentVisualProp = sanitizeCustomVisualPropsV2(visualProp, visualPropEditorDefinition, globalPersistedKeyVisualProp);
                        }
                        else {
                            currentVisualProp = sanitizeCustomVisualProps(visualProp, visualPropEditorDefinition, globalPersistedKeyVisualProp);
                        }
                    }
                    updateCustomVisualProps(currentVisualProp, isSystemAction);
                },
            });
        }
    }, [visualPropEditorDefinition, globalPersistedKeyVisualProp]);
    useEffect(() => {
        if (onEventCallBackFn) {
            onEventCallBackFn(ChartToTSEvent.UpdateVisualPropEditorDefinition, {
                chartModel: viz,
                currentVisualPropEditorDefinition: visualPropEditorDefinition,
                updateVisualPropEditorDefinitionContext,
                globalPersistedKeyVisualProp,
            });
        }
    }, [
        visualPropEditorDefinition,
        globalPersistedKeyVisualProp,
        JSON.stringify(viz?.customVisualProps),
    ]);
    useEffect(() => {
        const { onEvent, processEvent } = EventProcessor((errorMsg) => {
            setAnswerAlertBanner(errorMsg, BannerAlertType.ERROR);
        });
        setOnEventCallBackFn(() => {
            return onEvent;
        });
        logger.debug('Register chart component', componentId);
        const destroyListener = registerChartComponentListener(chartIframeElRef.current, componentId, processEvent);
        const fallbackTimeout = setTimeout(() => {
            clearInitAndRenderTimeouts();
            setError(getErrorObject(ErrorTypes.GENERIC_ERROR, chartType));
            onChartRenderComplete();
        }, CHART_INIT_START_TIMEOUT_INTERVAL);
        const clearFallbackTimeout = () => {
            clearTimeout(fallbackTimeout);
        };
        onEvent(ChartToTSEvent.InitStart, {
            onInitStart: () => {
                clearFallbackTimeout();
                logger.debug('ChartEvent.InitStart');
                onInitStartCallback();
            },
        });
        onEvent(ChartToTSEvent.RenderStart, {
            setRenderInProgress: () => {
                setError(error => {
                    if (renderTimeoutFired)
                        return error;
                    return null;
                });
            },
            setCustomChartLoadingInProgressMessage: (message) => {
                customChartLoadingInProgressMessageRef.current = message;
            },
        });
        onEvent(ChartToTSEvent.RenderComplete, {
            setIsRenderComplete: (renderComplete) => {
                iframeSpanRef.current?.setAttribute('iframe.render_complete', true);
                iframeSpanRef.current?.end();
                iframeSpanRef.current = null;
                onChartRenderComplete();
                if (isTsHostedChart) {
                    setCustomChartLoadingInProgressInfo({ isLoading: false });
                }
                clearInitAndRenderTimeouts();
                setIsRenderComplete(renderComplete);
            },
        });
        onEvent(ChartToTSEvent.RenderError, {
            setRenderErrorMessage: (errorMsg) => {
                iframeSpanRef.current?.setAttribute('iframe.error_type', ErrorTypes.CHART_RENDER_ERROR);
                iframeSpanRef.current?.setStatus({ code: 2 });
                iframeSpanRef.current?.end();
                iframeSpanRef.current = null;
                setIsRenderComplete(renderComplete);
                clearInitAndRenderTimeouts();
                onChartRenderComplete();
                const renderError = new Error(ErrorTypes.CHART_RENDER_ERROR);
                renderError.name = 'ChartRenderError';
                handleCustomChartError(renderError, ErrorTypes.CHART_RENDER_ERROR);
                setError(getErrorObject(ErrorTypes.CHART_RENDER_ERROR, chartType, errorMsg));
            },
        });
        onEvent(ChartToTSEvent.OpenContextMenu, {
            chartModel: viz,
            openContextMenu,
            iFrameWindow: chartIframeElRef,
            emitEvent,
        });
        onEvent(ChartToTSEvent.CloseContextMenu, {
            closeContextMenu,
        });
        onEvent(ChartToTSEvent.GetTMLString, {
            getTMLString,
        });
        onEvent(ChartToTSEvent.SetTMLString, {
            setTMLString,
        });
        onEvent(ChartToTSEvent.GetDataForQuery, {
            chartModel: viz,
            getAnswerDataByQuery,
            get otelContext() { return replayParentCtxRef.current ?? otelContextRef.current; },
            get otelEnabled() { return otelEnabledRef.current; },
        });
        onEvent(ChartToTSEvent.ShowToolTip, {
            chartModel: viz,
            showToolTip,
            iFrameWindow: containerRef,
        });
        onEvent(ChartToTSEvent.HideToolTip, {
            hideToolTip,
        });
        onEvent(ChartToTSEvent.OpenAxisMenu, {
            chartModel: viz,
            openAxisMenu: openCustomChartMenu,
            iFrameWindow: chartIframeElRef,
            emitEvent,
        });
        onEvent(ChartToTSEvent.CloseAxisMenu, {
            closeAxisMenu: closeCustomChartMenu,
        });
        onEvent(ChartToTSEvent.ActionHandler, {
            actionHandler,
            chartModel: viz,
        });
        onEvent(ChartToTSEvent.UpdateChartConfig, {
            updateChartConfig,
        });
        onEvent(ChartToTSEvent.TrackChartInteraction, {
            metricsService,
            isTsHostedChart,
        });
        onEvent(ChartToTSEvent.DispatchChartInteraction, {
            iFrameWindow: chartIframeElRef,
            onPointRightClick,
            onPointClick,
            onPointDoubleClick,
            chartModel: viz,
        });
        onEvent(NativeChartToTSEvent.GetBlinkFlags, {});
        onEvent(NativeChartToTSEvent.GetLDFlags, {});
        onEvent(ChartToTSEvent.GetLabelTranslation, {});
        let muzeAiChartFrameOrigin = '';
        try {
            muzeAiChartFrameOrigin = new URL(targetFrameUrl, window.location.href).origin;
        }
        catch {
            muzeAiChartFrameOrigin = '';
        }
        const teardownMuzeAiBridge = setupMuzeAiHostBridge({
            chartIframeElRef,
            chartFrameOrigin: muzeAiChartFrameOrigin,
        });
        return () => {
            destroyListener();
            clearFallbackTimeout();
            clearInitAndRenderTimeouts();
            setValidateChartConfigCB(() => Promise.resolve(''));
            teardownMuzeAiBridge();
        };
    }, [targetFrameUrl]);
    logger.debug('error', error);
    return (React.createElement("div", { style: {
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
        }, "data-testid": "custom-chart-wrapper" },
        React.createElement(CustomGlobalAlertBannerComponent, { onEvent: onEventCallBackFn, emitEvent: emitEvent }),
        React.createElement("div", { className: `${styles.chartContainer}`, ref: containerRef, "data-testid": "custom-chart-component" },
            chartJSX,
            React.createElement(TooltipComponent, { isVisible: isTooltipVisible, tooltipConfig: tooltipConfig, tooltipEl: tooltipEl }),
            React.createElement(CustomGlobalAlertToastComponent, { onEvent: onEventCallBackFn, emitEvent: emitEvent }),
            React.createElement(ChartErrorComponent, { ...error }))));
};
export const CustomChartComponent = React.memo(CustomChartComponentInner);
//# sourceMappingURL=custom-chart.component.js.map